import React, { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import api, { isUpgradeRequiredError } from "../utils/api";
import FlipBook from "../FlipBook";
import PageLayoutEditor from "../PageLayoutEditor";
import CoverPageDesigner from "../components/CoverPageDesigner";
import { useAuth } from "../AuthContext";
import useProfile from "../hooks/useProfile";
import { useStrings } from "../LanguageContext";
import TermsGate from "../components/TermsGate";
import ImageEditor from "../components/ImageEditor";
import "../BookManager.css";
import "./Home.css";
import ScrollRow from "../components/ScrollRow";
import { PAGE_SHAPES, AUTO_PAGE_SIZE_KEY } from "../constants/pageSizes";

const API = `${process.env.REACT_APP_API_URL}/api/books`;
const UPLOAD_API = `${process.env.REACT_APP_API_URL}/api/upload`;
const GENERATE_API = `${process.env.REACT_APP_API_URL}/api/generate-image`;

// Longest edge of a shape swatch, in px.
const SHAPE_SWATCH_PX = 54;

/**
 * The page shape picker on the document-upload form.
 *
 * <p>Drawn as swatches rather than listed in a dropdown because the choice IS a shape —
 * seeing a square next to a tall rectangle answers the question faster than reading
 * "1:1" and "2:3" does. "Match my file" leads, since it is right for most uploads.
 */
function PageShapePicker({ value, onChange, strings }) {
  const names = strings.bookManager.pageShapeNames || {};
  const descriptions = strings.bookManager.pageShapeDescriptions || {};
  const options = [
    { key: AUTO_PAGE_SIZE_KEY, auto: true },
    ...PAGE_SHAPES.map((shape) => ({ key: shape.key, shape })),
  ];

  return (
    <div className="bm-shape-grid" role="radiogroup" aria-label={strings.bookManager.pageSizeLabel}>
      {options.map(({ key, shape, auto }) => {
        const selected = value === key;
        const longEdge = shape ? Math.max(shape.frameWidth, shape.frameHeight) : 1;
        const swatch = auto
          ? { width: Math.round(SHAPE_SWATCH_PX * 0.78), height: SHAPE_SWATCH_PX }
          : {
              width: Math.round((shape.frameWidth / longEdge) * SHAPE_SWATCH_PX),
              height: Math.round((shape.frameHeight / longEdge) * SHAPE_SWATCH_PX),
            };
        return (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`bm-shape-card${selected ? " bm-shape-card-selected" : ""}`}
            onClick={() => onChange(key)}
          >
            <span
              className={`bm-shape-swatch${auto ? " bm-shape-swatch-auto" : ""}`}
              style={swatch}
              aria-hidden="true"
            >
              {auto ? "?" : null}
            </span>
            <span className="bm-shape-name">
              {auto ? strings.bookManager.pageSizeAuto : (names[key] || shape.label)}
            </span>
            <span className="bm-shape-desc">
              {auto
                ? strings.bookManager.pageSizeAutoCaption
                : (descriptions[key] || shape.description)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// Public books browser shown when no user is logged in, or when a logged-in
// user has not yet created a profile (tri-state create flow — #28).
function PublicBooks() {
  const strings = useStrings();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [readingBookId, setReadingBookId] = useState(null);

  useEffect(() => {
    const fetchPublished = async () => {
      try {
        const res = await api.get(`${API}/search?status=PUBLISHED`);
        const allBooks = Array.isArray(res.data) ? res.data : [];
        setBooks(allBooks.filter((b) => (b.category || "").toUpperCase() !== "MAGAZINE"));
      } catch {
        setError(strings.publicBooks.error);
      } finally {
        setLoading(false);
      }
    };
    fetchPublished();
  }, []);

  if (readingBookId) {
    const currentIndex = books.findIndex((b) => b.id === readingBookId);
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < books.length - 1;

    return (
      <div className="book-manager">
        <div className="bm-reader-nav">
          <button className="bm-btn bm-btn-back" onClick={() => setReadingBookId(null)}>
            {strings.publicBooks.backToBooks}
          </button>
          <button
            className="bm-btn bm-btn-back"
            disabled={!hasPrev}
            onClick={() => hasPrev && setReadingBookId(books[currentIndex - 1].id)}
          >
            {strings.readBook.prevBook}
          </button>
          <button
            className="bm-btn bm-btn-back"
            disabled={!hasNext}
            onClick={() => hasNext && setReadingBookId(books[currentIndex + 1].id)}
          >
            {strings.readBook.nextBook}
          </button>
        </div>
        <FlipBook bookId={readingBookId} />
      </div>
    );
  }

  return (
    <div className="book-manager">

      <div className="home-section home-section-books">
      <div className="home-section-header">
        <h2 className="home-section-heading">{strings.publicBooks.heading}</h2>
      </div>
      {loading && <p>{strings.publicBooks.loading}</p>}
      {error && <p className="bm-message">{error}</p>}

      {!loading && !error && books.length === 0 && (
        <p className="home-empty">{strings.publicBooks.emptyState}</p>
      )}

      {!loading && !error && books.length > 0 && (
        <ScrollRow className="home-books-row">
          {books.map((book) => (
            <div key={book.id} className="home-book-card">
              <button
                className="home-book-link"
                onClick={() => setReadingBookId(book.id)}
                aria-label={`Read ${book.title}`}
                style={{ background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer", width: "100%" }}
              >
                <div className="home-book-cover">
                  {book.coverImageUrl ? (
                    <img src={resolveImageUrl(book.coverImageUrl)} alt={book.title} className="home-book-cover-img" />
                  ) : (
                    <span className="home-book-cover-title">{book.title}</span>
                  )}
                </div>
                <div className="home-book-info">
                  <span className="home-book-title">{book.title}</span>
                  {book.authorName && (
                    <span className="home-book-author">by {book.authorName}</span>
                  )}
                </div>
              </button>
            </div>
          ))}
        </ScrollRow>
      )}
      </div>{/* end home-section */}
    </div>
  );
}

// Helper to resolve image URL (supports local uploads and Drive URLs)
function resolveImageUrl(url) {
  if (!url) return url;
  // Local upload path
  if (url.startsWith("/uploads/")) {
    return `${process.env.REACT_APP_API_URL}${url}`;
  }
  // Google Drive shareable link
  const match = url.match(/\/file\/d\/([^/]+)\//);
  if (match) {
    return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w400`;
  }
  return url;
}

const DEFAULT_FORMAT = {
  fontFamily: "sans-serif",
  fontSize: "16px",
  color: "#1a1a2e",
  layout: {},
};

function BookManager() {
  const { user, isAdmin, isSuperAdmin, isPremiumOrAbove } = useAuth();
  const { hasProfile, loading: profileLoading } = useProfile();
  const canCustomizeCover = isSuperAdmin || isPremiumOrAbove;
  // Book creation & document upload is a paid-tier (or admin) feature — Free is read-only.
  const canCreateBooks = isAdmin || isPremiumOrAbove;
  const strings = useStrings();
  const location = useLocation();
  const bmNavigate = useNavigate();
  const userId = user?.userId || 1;
  const [view, setView] = useState("menu");
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [pages, setPages] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [publishedBooks, setPublishedBooks] = useState([]);
  const [publishedLoading, setPublishedLoading] = useState(true);
  const [readingBookId, setReadingBookId] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  // Trim size for the book being uploaded. AUTO asks the server to measure the PDF,
  // which is right for most uploads; the presets are for a document whose page size
  // is not what it should be published at (or for a Word file, which has none).
  const [docPageSize, setDocPageSize] = useState(AUTO_PAGE_SIZE_KEY);
  // Appending a further document to the book currently open in the editor.
  const [appendUploading, setAppendUploading] = useState(false);

  // Fetch published books for the menu view
  useEffect(() => {
    if (!user) return;
    const fetchPublished = async () => {
      try {
        const res = await api.get(`${API}/search?status=PUBLISHED&userId=${userId}`);
        const allBooks = Array.isArray(res.data) ? res.data : [];
        setPublishedBooks(allBooks.filter((b) => (b.category || "").toUpperCase() !== "MAGAZINE"));
      } catch {
        setPublishedBooks([]);
      } finally {
        setPublishedLoading(false);
      }
    };
    fetchPublished();
  }, [user, userId]);

  // Page form state
  const [pageContent, setPageContent] = useState("");
  const [pageNumber, setPageNumber] = useState("");
  const [pageImageUrl, setPageImageUrl] = useState("");
  const [pageImageUrl2, setPageImageUrl2] = useState("");
  const [editingPage, setEditingPage] = useState(null);

  // Format state
  const [formatFontFamily, setFormatFontFamily] = useState("sans-serif");
  const [formatFontSize, setFormatFontSize] = useState("16px");
  const [formatColor, setFormatColor] = useState("#1a1a2e");
  const [pageLayout, setPageLayout] = useState({});

  // Upload state
  const [uploading1, setUploading1] = useState(false);
  const [uploading2, setUploading2] = useState(false);
  const [editUploading1, setEditUploading1] = useState(false);
  const [editUploading2, setEditUploading2] = useState(false);

  // AI generation state
  const [generating1, setGenerating1] = useState(false);
  const [generating2, setGenerating2] = useState(false);
  const [editGenerating1, setEditGenerating1] = useState(false);
  const [editGenerating2, setEditGenerating2] = useState(false);
  const [imageStyle, setImageStyle] = useState("general");

  // Image editor state
  const [editorFile, setEditorFile] = useState(null);
  const [editorCallback, setEditorCallback] = useState(null);

  // Cover/Back page designer state
  const [coverDesignData, setCoverDesignData] = useState({});

  // Help & Support state
  const [showHelp, setShowHelp] = useState(false);
  const [supportName, setSupportName] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [supportPhone, setSupportPhone] = useState("");
  const [supportRequestTypes, setSupportRequestTypes] = useState([]);
  const [supportOtherType, setSupportOtherType] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [supportWantAppointment, setSupportWantAppointment] = useState("");
  const [supportPrefDate, setSupportPrefDate] = useState("");
  const [supportPrefTime, setSupportPrefTime] = useState("");
  const [supportMeetingType, setSupportMeetingType] = useState("");
  const [supportTimeline, setSupportTimeline] = useState("");
  const [supportSending, setSupportSending] = useState(false);
  const [supportSent, setSupportSent] = useState(false);
  const [supportError, setSupportError] = useState("");

  // Helper: detect if a page number is the back page (last page in the book)
  const getBackPageNumber = () => {
    if (pages.length === 0) return 50;
    return pages[pages.length - 1]?.pageNumber || 50;
  };
  const isCoverPage = (num) => parseInt(num) === 1;
  const isBackPage = (num) => {
    const n = parseInt(num);
    return n === getBackPageNumber() || n === 50 || n === 99;
  };
  const isCoverOrBack = (num) => isCoverPage(num) || isBackPage(num);

  // Open book for editing when navigated from Account page
  useEffect(() => {
    const editBookId = location.state?.editBookId;
    if (editBookId) {
      const openBook = async () => {
        try {
          const res = await api.get(`${API}/${editBookId}`);
          setSelectedBook(res.data);
          const pagesRes = await api.get(`${API}/${editBookId}/pages`);
          setPages(Array.isArray(pagesRes.data) ? pagesRes.data : []);
          setView("edit");
        } catch {
          // fall back to menu if book not found
        }
      };
      openBook();
      // Clear the state so refreshing doesn't re-trigger
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const showMessage = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(""), 3000);
  };

  const buildFormatJson = (family, size, color, layout) => {
    return JSON.stringify({ fontFamily: family, fontSize: size, color: color, layout: layout || {} });
  };

  const parseFormatJson = (formatStr) => {
    try {
      const parsed = JSON.parse(formatStr);
      return {
        fontFamily: parsed.fontFamily || "sans-serif",
        fontSize: parsed.fontSize || "16px",
        color: parsed.color || "#1a1a2e",
        layout: parsed.layout || {},
      };
    } catch {
      return { ...DEFAULT_FORMAT };
    }
  };

  const handleUpload = async (file, setUrl, setUploadingState) => {
    if (!file) return;
    setUploadingState(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(UPLOAD_API, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUrl(res.data.url);
      showMessage(strings.bookManager.msgImageUploaded);
    } catch (err) {
      showMessage(strings.bookManager.msgUploadFailed(err.response?.data?.error || err.message));
    } finally {
      setUploadingState(false);
    }
  };

  const handleGenerateImage = async (content, setUrl, setGeneratingState) => {
    const prompt = window.prompt(strings.bookManager.aiPromptDialog, content?.trim() || "");
    if (prompt === null) return; // user cancelled
    if (!prompt.trim()) {
      showMessage(strings.bookManager.msgEmptyContentForAI);
      return;
    }
    setGeneratingState(true);
    try {
      const res = await api.post(GENERATE_API, { prompt: prompt.trim(), style: imageStyle }, { timeout: 90000 });
      setUrl(res.data.url);
      showMessage(strings.bookManager.msgImageGenerated);
    } catch (err) {
      showMessage(strings.bookManager.msgGenerateFailed(err.response?.data?.error || err.message));
    } finally {
      setGeneratingState(false);
    }
  };

  const fetchBooks = async () => {
    try {
      setLoading(true);
      const res = await api.get(`${API}/user/${userId}`);
      setBooks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setBooks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrafts = async () => {
    try {
      setLoading(true);
      const res = await api.get(`${API}/user/${userId}/drafts`);
      setBooks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setBooks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookPages = async (bookId) => {
    try {
      const res = await api.get(`${API}/${bookId}/pages`);
      setPages(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setPages([]);
    }
  };

  const handleCreateBook = async () => {
    if (!newTitle.trim()) {
      showMessage(strings.bookManager.msgEnterTitle);
      return;
    }
    try {
      setLoading(true);
      const res = await api.post(`${API}/create`, { title: newTitle, userId });
      setSelectedBook(res.data);
      setPages(res.data.pages || []);
      setNewTitle("");
      showMessage(strings.bookManager.msgBookCreated);
      setView("edit");
    } catch (err) {
      // Plan-limit hits surface via the global upgrade modal — skip the inline message.
      if (!isUpgradeRequiredError(err)) showMessage(strings.bookManager.msgCreateFailed(err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEditBook = async (book) => {
    setSelectedBook(book);
    await fetchBookPages(book.id);
    setView("edit");
  };

  const handleUpdateTitle = async () => {
    if (!selectedBook) return;
    try {
      const res = await api.put(`${API}/${selectedBook.id}`, { title: selectedBook.title, userId: String(userId) });
      setSelectedBook(res.data);
      showMessage(strings.bookManager.msgTitleUpdated);
    } catch (err) {
      showMessage(strings.bookManager.msgTitleFailed);
    }
  };

  const handlePublish = async () => {
    if (!selectedBook) return;
    try {
      const res = await api.put(`${API}/${selectedBook.id}/publish?userId=${userId}`);
      setSelectedBook(res.data);
      showMessage(strings.bookManager.msgPublished);
    } catch (err) {
      showMessage(strings.bookManager.msgPublishFailed);
    }
  };

  const handleSaveDraft = async () => {
    if (!selectedBook) return;
    try {
      const res = await api.put(`${API}/${selectedBook.id}/draft?userId=${userId}`);
      setSelectedBook(res.data);
      showMessage(strings.bookManager.msgDraftSaved);
    } catch (err) {
      showMessage(strings.bookManager.msgDraftFailed);
    }
  };

  const handleDeleteBook = async (bookId) => {
    if (!window.confirm(strings.bookManager.confirmDeleteBook)) return;
    try {
      await api.delete(`${API}/${bookId}?userId=${userId}`);
      showMessage(strings.bookManager.msgBookDeleted);
      setSelectedBook(null);
      setPages([]);
      setView("menu");
    } catch (err) {
      showMessage(strings.bookManager.msgDeleteBookFailed);
    }
  };

  const handleAddPage = async () => {
    if (!selectedBook) return;
    if (!pageNumber) {
      showMessage(strings.bookManager.msgPageNumberRequired);
      return;
    }
    try {
      const isSpecialPage = isCoverOrBack(pageNumber);
      const formatJson = isSpecialPage
        ? JSON.stringify({ fontFamily: "sans-serif", fontSize: "16px", color: "#1a1a2e", coverDesign: coverDesignData, layout: {} })
        : buildFormatJson(formatFontFamily, formatFontSize, formatColor, pageLayout);
      await api.post(`${API}/${selectedBook.id}/page?userId=${userId}`, {
        pageNumber: parseInt(pageNumber),
        content: pageContent,
        format: formatJson,
        imageUrl: pageImageUrl,
        imageUrl2: isSpecialPage ? "" : pageImageUrl2,
      });
      showMessage(strings.bookManager.msgPageAdded);
      setPageContent("");
      setPageNumber("");
      setPageImageUrl("");
      setPageImageUrl2("");
      setFormatFontFamily("sans-serif");
      setFormatFontSize("16px");
      setFormatColor("#1a1a2e");
      setPageLayout({});
      setCoverDesignData({});
      await fetchBookPages(selectedBook.id);
    } catch (err) {
      if (!isUpgradeRequiredError(err)) showMessage(strings.bookManager.msgAddPageFailed);
    }
  };

  const handleUpdatePage = async () => {
    if (!editingPage) return;
    try {
      await api.put(`${API}/page/${editingPage.id}?userId=${userId}`, {
        pageNumber: editingPage.pageNumber,
        content: editingPage.content,
        format: editingPage.format,
        imageUrl: editingPage.imageUrl,
        imageUrl2: editingPage.imageUrl2,
      });
      showMessage(strings.bookManager.msgPageUpdated);
      setEditingPage(null);
      await fetchBookPages(selectedBook.id);
    } catch (err) {
      showMessage(strings.bookManager.msgUpdatePageFailed);
    }
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm(strings.bookManager.confirmDeletePage)) return;
    try {
      await api.delete(`${API}/page/${pageId}?userId=${userId}`);
      showMessage(strings.bookManager.msgPageDeleted);
      await fetchBookPages(selectedBook.id);
    } catch (err) {
      showMessage(strings.bookManager.msgDeletePageFailed);
    }
  };

  const startEditingPage = (page) => {
    const fmt = parseFormatJson(page.format);
    setEditingPage({
      ...page,
      _fontFamily: fmt.fontFamily,
      _fontSize: fmt.fontSize,
      _color: fmt.color,
      _layout: fmt.layout || {},
    });
  };

  const updateEditFormat = (key, value) => {
    setEditingPage((prev) => {
      const updated = { ...prev, [key]: value };
      updated.format = buildFormatJson(
        updated._fontFamily,
        updated._fontSize,
        updated._color,
        updated._layout
      );
      return updated;
    });
  };

  // Render the image upload area
  const renderImageUpload = (label, url, setUrl, uploading, setUploading, inputId, contentForAI, generating, setGenerating) => (
    <div className="bm-upload-area">
      <label className="bm-upload-label">{label}</label>
      {url ? (
        <div className="bm-upload-preview">
          <img src={resolveImageUrl(url)} alt={label} className="bm-upload-thumb" />
          <button
            className="bm-btn bm-btn-delete bm-btn-sm"
            onClick={() => setUrl("")}
            type="button"
          >
            {strings.common.remove}
          </button>
        </div>
      ) : (
        <div className="bm-upload-controls">
          <input
            type="file"
            accept="image/*"
            id={inputId}
            className="bm-file-input"
            onChange={(e) => {
              if (e.target.files[0]) {
                setEditorFile(e.target.files[0]);
                setEditorCallback(() => (editedFile) => {
                  handleUpload(editedFile, setUrl, setUploading);
                  setEditorFile(null);
                  setEditorCallback(null);
                });
                e.target.value = "";
              }
            }}
          />
          <label htmlFor={inputId} className="bm-btn bm-btn-edit bm-btn-sm">
            {strings.bookManager.chooseFile}
          </label>
          {contentForAI !== undefined && (
            <div className="bm-ai-controls">
              <select
                className="bm-format-select"
                value={imageStyle}
                onChange={(e) => setImageStyle(e.target.value)}
                aria-label={strings.bookManager.imageStyleLabel}
              >
                {strings.bookManager.imageStyles.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <button
                className="bm-btn bm-btn-ai bm-btn-sm"
                type="button"
                disabled={generating || uploading}
                onClick={() => handleGenerateImage(contentForAI, setUrl, setGenerating)}
              >
                {generating ? strings.bookManager.generatingImage : strings.bookManager.createWithAI}
              </button>
            </div>
          )}
          {uploading && <span className="bm-uploading">{strings.bookManager.uploading}</span>}
          {generating && <span className="bm-uploading">{strings.bookManager.generatingImage}</span>}
        </div>
      )}
    </div>
  );

  // Render formatting toolbar
  const renderFormatToolbar = (family, setFamily, size, setSize, color, setColor) => (
    <div className="bm-format-toolbar">
      <label className="bm-format-label">{strings.bookManager.textFormatting}</label>
      <div className="bm-format-controls">
        <select
          value={family}
          onChange={(e) => setFamily(e.target.value)}
          className="bm-format-select"
          aria-label={strings.bookManager.ariaFontFamily}
        >
          <option value="sans-serif">{strings.bookManager.fontSans}</option>
          <option value="serif">{strings.bookManager.fontSerif}</option>
          <option value="monospace">{strings.bookManager.fontMono}</option>
          <option value="cursive">{strings.bookManager.fontCursive}</option>
        </select>
        <select
          value={size}
          onChange={(e) => setSize(e.target.value)}
          className="bm-format-select"
          aria-label={strings.bookManager.ariaFontSize}
        >
          <option value="12px">12px</option>
          <option value="14px">14px</option>
          <option value="16px">16px</option>
          <option value="18px">18px</option>
          <option value="20px">20px</option>
          <option value="24px">24px</option>
          <option value="28px">28px</option>
          <option value="32px">32px</option>
          <option value="36px">36px</option>
        </select>
        <div className="bm-color-picker-wrap">
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="bm-color-picker"
            aria-label={strings.bookManager.ariaFontColor}
          />
          <span className="bm-color-label">{color}</span>
        </div>
      </div>
    </div>
  );

  // Show public books browser if not logged in, or if logged in without a profile
  // (tri-state create flow — #28). Wait for profile fetch so we don't flash the
  // full manager UI to users who haven't completed profile yet.
  if (!user) return <PublicBooks />;
  if (profileLoading) return null;
  if (!hasProfile) return <PublicBooks />;

  const wrappedContent = (() => {
  // Main menu
  if (view === "menu") {
    const isReading = !!readingBookId;
    return (
      <div className={`book-manager ${isReading ? "bm-layout-reading" : ""}`}>
        <div className={isReading ? "bm-sidebar" : ""}>
          <h1>{strings.bookManager.heading}</h1>
          {message && <div className="bm-message">{message}</div>}
          <div className="bm-section-card">
          <div className={`bm-button-row ${isReading ? "bm-button-col" : ""}`}>
            {canCreateBooks ? (
              <>
            <button className="bm-btn bm-btn-create" onClick={() => { setReadingBookId(null); setView("create"); }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
              {strings.bookManager.createNewBook}
            </button>
            <button className="bm-btn bm-btn-upload" onClick={() => { setReadingBookId(null); setNewTitle(""); setDocFile(null); setDocPageSize(AUTO_PAGE_SIZE_KEY); setView("upload-doc"); }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 12 15 15"/></svg>
              {strings.bookManager.createFromDocument}
            </button>
              </>
            ) : (
            <button className="bm-btn bm-btn-create" onClick={() => bmNavigate("/pricing")}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7.4-6.3-4.6L5.7 21.4 8 14 2 9.4h7.6z"/></svg>
              Upgrade to create books
            </button>
            )}
            <button className="bm-btn bm-btn-draft" onClick={() => { setReadingBookId(null); fetchDrafts(); setView("drafts"); }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              {strings.bookManager.myDrafts}
            </button>
            <button className="bm-btn bm-btn-all" onClick={() => { setReadingBookId(null); fetchBooks(); setView("allbooks"); }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>
              {strings.bookManager.allMyBooks}
            </button>
            <button className="bm-btn bm-btn-edit" onClick={() => bmNavigate("/articles")}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
              My Articles
            </button>
            <button className="bm-btn bm-btn-help" onClick={() => bmNavigate("/help-support")}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              Help & Support
            </button>
          </div>
          </div>{/* end bm-section-card for buttons */}

          <div className="home-section home-section-books">
          <div className="home-section-header">
            <h2 className="home-section-heading">{strings.publicBooks.heading}</h2>
          </div>
          {publishedLoading && <p>{strings.publicBooks.loading}</p>}
          {!publishedLoading && publishedBooks.length === 0 && (
            <p className="home-empty">{strings.publicBooks.emptyState}</p>
          )}
          {!publishedLoading && publishedBooks.length > 0 && (
            isReading ? (
              <div className="bm-book-btn-list">
                {publishedBooks.map((book) => (
                  <button
                    key={book.id}
                    className={`bm-btn bm-btn-book-item ${readingBookId === book.id ? "bm-btn-book-active" : ""}`}
                    onClick={() => setReadingBookId(book.id)}
                  >
                    {book.title}
                  </button>
                ))}
              </div>
            ) : (
              <ScrollRow className="home-books-row">
                {publishedBooks.map((book) => (
                  <div key={book.id} className="home-book-card">
                    <button
                      className="home-book-link"
                      onClick={() => setReadingBookId(book.id)}
                      aria-label={`Read ${book.title}`}
                      style={{ background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer", width: "100%" }}
                    >
                      <div className="home-book-cover">
                        {book.coverImageUrl ? (
                          <img src={resolveImageUrl(book.coverImageUrl)} alt={book.title} className="home-book-cover-img" />
                        ) : (
                          <span className="home-book-cover-title">{book.title}</span>
                        )}
                      </div>
                      <div className="home-book-info">
                        <span className="home-book-title">{book.title}</span>
                        {book.authorName && (
                          <span className="home-book-author">by {book.authorName}</span>
                        )}
                      </div>
                    </button>
                  </div>
                ))}
              </ScrollRow>
            )
          )}
          </div>{/* end home-section for published books */}
        </div>

        {isReading && (() => {
          const currentIndex = publishedBooks.findIndex((b) => b.id === readingBookId);
          const hasPrev = currentIndex > 0;
          const hasNext = currentIndex < publishedBooks.length - 1;
          return (
            <div className="bm-reader-main">
              <div className="bm-reader-nav">
                <button className="bm-btn bm-btn-back" onClick={() => setReadingBookId(null)}>
                  {strings.publicBooks.backToBooks}
                </button>
                <button
                  className="bm-btn bm-btn-back"
                  disabled={!hasPrev}
                  onClick={() => hasPrev && setReadingBookId(publishedBooks[currentIndex - 1].id)}
                >
                  {strings.readBook.prevBook}
                </button>
                <button
                  className="bm-btn bm-btn-back"
                  disabled={!hasNext}
                  onClick={() => hasNext && setReadingBookId(publishedBooks[currentIndex + 1].id)}
                >
                  {strings.readBook.nextBook}
                </button>
              </div>
              <FlipBook bookId={readingBookId} />
            </div>
          );
        })()}
      </div>
    );
  }

  // Create new book
  if (view === "create") {
    return (
      <div className="book-manager">
        <h1>{strings.bookManager.createHeading}</h1>
        {message && <div className="bm-message">{message}</div>}
        <div className="bm-form">
          <input
            type="text"
            placeholder={strings.bookManager.placeholderTitle}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            className="bm-input"
          />
          <button className="bm-btn bm-btn-create" onClick={handleCreateBook} disabled={loading}>
            {loading ? strings.bookManager.creating : strings.bookManager.createButton}
          </button>
          <button className="bm-btn bm-btn-back" onClick={() => setView("menu")}>{strings.common.back}</button>
          <button className="bm-btn bm-btn-help" onClick={() => setShowHelp(!showHelp)} type="button">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            Help & Support
          </button>
        </div>

        {showHelp && (
          <div className="bm-help-panel">
            <h3>How to Create a Book</h3>
            <ol>
              <li><strong>Enter a title</strong> for your book and click "Create".</li>
              <li><strong>Add pages</strong> with text, images, or AI-generated illustrations.</li>
              <li><strong>Design your cover</strong> (page 1) and back page using the Cover Designer (Premium feature).</li>
              <li><strong>Preview</strong> your book with the FlipBook viewer before publishing.</li>
              <li><strong>Publish</strong> when ready to share with the community.</li>
            </ol>
            <h3>Cover & Back Page Tips</h3>
            <ul>
              <li>Generate or upload a background image for your cover.</li>
              <li>Click "Save & Customize" to position text on the image.</li>
              <li><strong>Drag text blocks</strong> to move them to the desired position.</li>
              <li>Adjust image scale with the slider for the perfect fit.</li>
              <li>Click "Save as Cover Page Image" to save the final composite.</li>
            </ul>
            <h3>Reading Features</h3>
            <ul>
              <li>Use the <strong>Read</strong> button to have a page read aloud.</li>
              <li>Use <strong>Podcast</strong> mode to listen to the entire book continuously.</li>
            </ul>
            <p className="bm-help-contact">Need more help? Contact us at <strong>avikaventures.info@gmail.com</strong></p>
          </div>
        )}

        {/* Help & Support / Content Creation Request Form */}
        <div className="bm-support-form-section">
          <h2 className="bm-support-form-title">Help & Support / Content Creation Request</h2>
          <p className="bm-support-desc">
            Need help or want to request content creation? Fill out the form below and our team will get back to you.
          </p>
          {supportSent ? (
            <div className="bm-support-sent">
              Thank you for your request! We'll get back to you within 24-48 hours.
              <button className="bm-btn bm-btn-back" style={{ marginTop: 12 }} onClick={() => setSupportSent(false)}>Submit Another Request</button>
            </div>
          ) : (
            <form className="bm-contact-form" onSubmit={async (e) => {
              e.preventDefault();
              setSupportError("");
              if (!supportName.trim() || !supportEmail.trim() || !supportMessage.trim()) {
                setSupportError("Please fill in all required fields.");
                return;
              }
              if (supportRequestTypes.length === 0) {
                setSupportError("Please select at least one request type.");
                return;
              }
              setSupportSending(true);
              try {
                const requestTypes = supportRequestTypes.includes("Other")
                  ? [...supportRequestTypes.filter(t => t !== "Other"), `Other: ${supportOtherType}`].join(", ")
                  : supportRequestTypes.join(", ");
                const appointmentInfo = supportWantAppointment === "Yes"
                  ? `\n\nAppointment Requested:\nDate: ${supportPrefDate}\nTime: ${supportPrefTime}\nMeeting Type: ${supportMeetingType}`
                  : "\n\nAppointment: No";
                const fullMessage = `Request Type: ${requestTypes}\nTimeline: ${supportTimeline || "Not specified"}\nPhone: ${supportPhone || "Not provided"}\n\nProject Details:\n${supportMessage.trim()}${appointmentInfo}`;
                await api.post(`${process.env.REACT_APP_API_URL}/api/contact`, {
                  name: supportName.trim(),
                  email: supportEmail.trim(),
                  subject: `Help & Support: ${requestTypes}`,
                  message: fullMessage,
                });
                setSupportSent(true);
                setSupportName(""); setSupportEmail(""); setSupportPhone("");
                setSupportRequestTypes([]); setSupportOtherType("");
                setSupportMessage(""); setSupportWantAppointment(""); setSupportPrefDate("");
                setSupportPrefTime(""); setSupportMeetingType(""); setSupportTimeline("");
              } catch (err) {
                setSupportError(err.response?.data?.error || "Failed to send. Please try again.");
              } finally {
                setSupportSending(false);
              }
            }}>
              {supportError && <div className="bm-message" style={{ background: "#f8d7da", color: "#721c24", borderLeftColor: "#721c24" }}>{supportError}</div>}

              {/* Section 1: Contact Information */}
              <fieldset className="bm-form-section">
                <legend className="bm-form-section-title">Section 1: Contact Information</legend>
                <div className="bm-contact-field">
                  <label htmlFor="support-name">Full Name *</label>
                  <input id="support-name" type="text" className="bm-input" placeholder="Your full name" value={supportName} onChange={(e) => setSupportName(e.target.value)} required />
                </div>
                <div className="bm-contact-field">
                  <label htmlFor="support-email">Email Address *</label>
                  <input id="support-email" type="email" className="bm-input" placeholder="you@example.com" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} required />
                </div>
                <div className="bm-contact-field">
                  <label htmlFor="support-phone">Phone Number</label>
                  <input id="support-phone" type="tel" className="bm-input" placeholder="Your phone number" value={supportPhone} onChange={(e) => setSupportPhone(e.target.value)} />
                </div>
              </fieldset>

              {/* Section 2: Type of Request */}
              <fieldset className="bm-form-section">
                <legend className="bm-form-section-title">Section 2: Type of Request</legend>
                <div className="bm-contact-field">
                  <label>What do you need help with? * <span className="bm-field-hint">(Select one or more)</span></label>
                  <div className="bm-checkbox-group">
                    {["Content Creation (Book cover, poem, Article, blog posts)", "Technical Support (My account, Content Issue)"].map((option) => (
                      <label key={option} className="bm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={supportRequestTypes.includes(option)}
                          onChange={(e) => {
                            if (e.target.checked) setSupportRequestTypes([...supportRequestTypes, option]);
                            else setSupportRequestTypes(supportRequestTypes.filter(t => t !== option));
                          }}
                        />
                        {option}
                      </label>
                    ))}
                    <label className="bm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={supportRequestTypes.includes("Other")}
                        onChange={(e) => {
                          if (e.target.checked) setSupportRequestTypes([...supportRequestTypes, "Other"]);
                          else { setSupportRequestTypes(supportRequestTypes.filter(t => t !== "Other")); setSupportOtherType(""); }
                        }}
                      />
                      Other (please specify)
                    </label>
                    {supportRequestTypes.includes("Other") && (
                      <input type="text" className="bm-input" placeholder="Please specify..." value={supportOtherType} onChange={(e) => setSupportOtherType(e.target.value)} style={{ marginTop: 6 }} />
                    )}
                  </div>
                </div>
              </fieldset>

              {/* Section 3: Project Details */}
              <fieldset className="bm-form-section">
                <legend className="bm-form-section-title">Section 3: Project Details</legend>
                <div className="bm-contact-field">
                  <label htmlFor="support-message">Briefly describe your request *</label>
                  <p className="bm-field-hint">Example: "Need help creating book cover" or "Help setting up my page"</p>
                  <textarea id="support-message" className="bm-input bm-textarea" placeholder="Describe your request..." value={supportMessage} onChange={(e) => setSupportMessage(e.target.value)} required rows={5} />
                </div>
              </fieldset>

              {/* Section 4: Appointment Request */}
              <fieldset className="bm-form-section">
                <legend className="bm-form-section-title">Section 4: Appointment Request</legend>
                <div className="bm-contact-field">
                  <label>Would you like to schedule a consultation?</label>
                  <div className="bm-radio-group">
                    <label className="bm-radio-label">
                      <input type="radio" name="appointment" value="Yes" checked={supportWantAppointment === "Yes"} onChange={(e) => setSupportWantAppointment(e.target.value)} />
                      Yes
                    </label>
                    <label className="bm-radio-label">
                      <input type="radio" name="appointment" value="No" checked={supportWantAppointment === "No"} onChange={(e) => setSupportWantAppointment(e.target.value)} />
                      No
                    </label>
                  </div>
                </div>
                {supportWantAppointment === "Yes" && (
                  <div className="bm-appointment-details">
                    <div className="bm-contact-field">
                      <label htmlFor="support-date">Preferred Date</label>
                      <input id="support-date" type="date" className="bm-input" value={supportPrefDate} onChange={(e) => setSupportPrefDate(e.target.value)} />
                    </div>
                    <div className="bm-contact-field">
                      <label htmlFor="support-time">Preferred Time</label>
                      <input id="support-time" type="time" className="bm-input" value={supportPrefTime} onChange={(e) => setSupportPrefTime(e.target.value)} />
                    </div>
                    <div className="bm-contact-field">
                      <label>Meeting Type</label>
                      <div className="bm-radio-group">
                        <label className="bm-radio-label">
                          <input type="radio" name="meetingType" value="Phone Call" checked={supportMeetingType === "Phone Call"} onChange={(e) => setSupportMeetingType(e.target.value)} />
                          Phone Call
                        </label>
                        <label className="bm-radio-label">
                          <input type="radio" name="meetingType" value="Video Call" checked={supportMeetingType === "Video Call"} onChange={(e) => setSupportMeetingType(e.target.value)} />
                          Video Call
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </fieldset>

              {/* Section 5: Timeline & Priority */}
              <fieldset className="bm-form-section">
                <legend className="bm-form-section-title">Section 5: Timeline & Priority</legend>
                <div className="bm-contact-field">
                  <label>When do you need this completed?</label>
                  <div className="bm-radio-group">
                    {["As soon as possible", "Within a week", "Flexible"].map((option) => (
                      <label key={option} className="bm-radio-label">
                        <input type="radio" name="timeline" value={option} checked={supportTimeline === option} onChange={(e) => setSupportTimeline(e.target.value)} />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>
              </fieldset>

              <button type="submit" className="bm-btn bm-btn-create" disabled={supportSending}>
                {supportSending ? "Sending..." : "Submit Request"}
              </button>
            </form>
          )}
          <p className="bm-support-note">
            <em>Or email us directly at <strong>avikaventures.info@gmail.com</strong></em>
          </p>
        </div>
      </div>
    );
  }

  // Upload document to create book
  if (view === "upload-doc") {
    const handleDocUpload = async () => {
      if (!newTitle.trim()) {
        showMessage(strings.bookManager.msgEnterTitle);
        return;
      }
      if (!docFile) {
        showMessage(strings.bookManager.msgDocUploadFailed("No file selected"));
        return;
      }
      setDocUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", docFile);
        formData.append("title", newTitle.trim());
        formData.append("userId", userId);
        formData.append("pageSize", docPageSize);
        const res = await api.post(`${API}/upload-document`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const book = res.data;
        setSelectedBook(book);
        setPages(book.pages || []);
        const pageCount = (book.pages || []).length;
        showMessage(strings.bookManager.msgBookFromDoc(pageCount));
        setNewTitle("");
        setDocFile(null);
        setDocPageSize(AUTO_PAGE_SIZE_KEY);
        setView("edit");
      } catch (err) {
        if (!isUpgradeRequiredError(err)) showMessage(strings.bookManager.msgDocUploadFailed(err.response?.data?.error || err.message));
      } finally {
        setDocUploading(false);
      }
    };

    return (
      <div className="book-manager">
        <h1>{strings.bookManager.uploadDocHeading}</h1>
        {message && <div className="bm-message">{message}</div>}
        <div className="bm-form">
          <input
            type="text"
            placeholder={strings.bookManager.placeholderTitle}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            className="bm-input"
          />
          <div className="bm-doc-upload-area">
            <input
              type="file"
              accept=".pdf,.docx,.doc"
              id="doc-upload-input"
              className="bm-file-input"
              onChange={(e) => {
                if (e.target.files[0]) setDocFile(e.target.files[0]);
              }}
            />
            <label htmlFor="doc-upload-input" className="bm-btn bm-btn-edit">
              {strings.bookManager.chooseFile}
            </label>
            {docFile && <span className="bm-doc-filename">{docFile.name}</span>}
          </div>
          <div className="bm-pagesize">
            <span className="bm-pagesize-label">{strings.bookManager.pageSizeLabel}</span>
            <PageShapePicker value={docPageSize} onChange={setDocPageSize} strings={strings} />
            <p className="bm-doc-hint bm-pagesize-hint">{strings.bookManager.pageSizeHint}</p>
          </div>
          <p className="bm-doc-hint">{strings.bookManager.uploadDocHint}</p>
          <button
            className="bm-btn bm-btn-upload"
            onClick={handleDocUpload}
            disabled={docUploading}
          >
            {docUploading ? strings.bookManager.uploadingDoc : strings.bookManager.uploadDocButton}
          </button>
          <button className="bm-btn bm-btn-back" onClick={() => setView("menu")}>
            {strings.common.back}
          </button>
          <button className="bm-btn bm-btn-help" onClick={() => setShowHelp(!showHelp)} type="button">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            Help & Support
          </button>
        </div>

        {showHelp && (
          <div className="bm-help-panel">
            <h3>How to Create from Document</h3>
            <ol>
              <li><strong>Enter a title</strong> for your book.</li>
              <li><strong>Upload a PDF or Word document</strong> (.pdf, .docx, .doc).</li>
              <li>Your document will be <strong>automatically split into pages</strong>.</li>
              <li><strong>Edit and format</strong> each page as needed.</li>
              <li><strong>Preview and publish</strong> when ready.</li>
            </ol>
            <p className="bm-help-contact">Need more help? Contact us at <strong>avikaventures.info@gmail.com</strong></p>
          </div>
        )}
      </div>
    );
  }

  // Drafts list
  if (view === "drafts") {
    return (
      <div className="book-manager">
        <h1>{strings.bookManager.draftsHeading}</h1>
        {message && <div className="bm-message">{message}</div>}
        {loading ? <p>{strings.common.loading}</p> : (
          <div className="bm-book-list">
            {books.length === 0 && <p>{strings.bookManager.noDrafts}</p>}
            {books.map((book) => (
              <div key={book.id} className="bm-book-card">
                <div className="bm-book-info">
                  <h3>{book.title}</h3>
                  <span className="bm-status bm-status-draft">DRAFT</span>
                  <p className="bm-date">{strings.bookManager.modified}{book.modifiedDate}</p>
                </div>
                <div className="bm-book-actions">
                  <button className="bm-btn bm-btn-edit" onClick={() => handleEditBook(book)}>{strings.common.edit}</button>
                  <button className="bm-btn bm-btn-delete" onClick={() => handleDeleteBook(book.id)}>{strings.common.delete}</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <button className="bm-btn bm-btn-back" onClick={() => setView("menu")}>{strings.common.back}</button>
      </div>
    );
  }

  // All books list
  if (view === "allbooks") {
    return (
      <div className="book-manager">
        <h1>{strings.bookManager.allBooksHeading}</h1>
        {!canCreateBooks && (
          <div className="bm-upgrade-banner" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", padding: "12px 16px", borderRadius: 10, margin: "8px 0 16px" }}>
            <span>Upgrade your plan to create and publish books.</span>
            <button className="bm-btn bm-btn-create" onClick={() => bmNavigate("/pricing")}>View plans</button>
          </div>
        )}
        {message && <div className="bm-message">{message}</div>}
        {loading ? <p>{strings.common.loading}</p> : (
          <div className="bm-book-list">
            {books.length === 0 && <p>{strings.bookManager.noBooks}</p>}
            {books.map((book) => (
              <div key={book.id} className="bm-book-card">
                <div className="bm-book-info">
                  <h3>{book.title}</h3>
                  <span className={`bm-status ${book.status === "PUBLISHED" ? "bm-status-published" : "bm-status-draft"}`}>
                    {book.status}
                  </span>
                  <p className="bm-date">{strings.bookManager.modified}{book.modifiedDate}</p>
                </div>
                <div className="bm-book-actions">
                  <button className="bm-btn bm-btn-edit" onClick={() => handleEditBook(book)}>{strings.common.edit}</button>
                  <button className="bm-btn bm-btn-preview" onClick={() => { setSelectedBook(book); setView("preview"); }}>{strings.bookManager.preview}</button>
                  <button className="bm-btn bm-btn-delete" onClick={() => handleDeleteBook(book.id)}>{strings.common.delete}</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <button className="bm-btn bm-btn-back" onClick={() => setView("menu")}>{strings.common.back}</button>
      </div>
    );
  }

  // Edit book
  if (view === "edit") {
    return (
      <div className="book-manager">
        <h1>{strings.bookManager.editHeading}</h1>
        {message && <div className="bm-message">{message}</div>}

        {selectedBook && (
          <div className="bm-edit-section">
            {/* Book details */}
            <div className="bm-edit-header">
              <input
                type="text"
                value={selectedBook.title}
                onChange={(e) => setSelectedBook({ ...selectedBook, title: e.target.value })}
                className="bm-input bm-input-title"
              />
              <button className="bm-btn bm-btn-edit" onClick={handleUpdateTitle}>{strings.bookManager.saveTitle}</button>
              <span className={`bm-status ${selectedBook.status === "PUBLISHED" ? "bm-status-published" : "bm-status-draft"}`}>
                {selectedBook.status}
              </span>
            </div>

            <div className="bm-action-bar">
              <button className="bm-btn bm-btn-draft" onClick={handleSaveDraft}>{strings.bookManager.saveAsDraft}</button>
              <button className="bm-btn bm-btn-create" onClick={handlePublish}>{strings.bookManager.publish}</button>
              <button className="bm-btn bm-btn-preview" onClick={() => setView("preview")}>{strings.bookManager.preview}</button>
              <button className="bm-btn bm-btn-delete" onClick={() => handleDeleteBook(selectedBook.id)}>{strings.bookManager.deleteBook}</button>
              <button className="bm-btn bm-btn-help" onClick={() => setShowHelp(!showHelp)} type="button">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                Help
              </button>
            </div>

            {showHelp && (
              <div className="bm-help-panel">
                <h3>Book Editing Guide</h3>
                <ul>
                  <li><strong>Page 1</strong> is your Cover Page. Set its page number to 1 to use the Cover Designer.</li>
                  <li>The <strong>last page</strong> is your Back Page. Design it with the Back Page Designer.</li>
                  <li>Use <strong>Save & Customize</strong> to position text on cover/back page images.</li>
                  <li><strong>Drag text blocks</strong> to reposition title, author name, etc. on the cover.</li>
                  <li>Click <strong>Save as Cover/Back Page Image</strong> to save the final composite.</li>
                  <li>Use <strong>Preview</strong> to see your book as readers will see it.</li>
                </ul>
                <h3>Reading & Audio</h3>
                <ul>
                  <li>In preview or read mode, use <strong>Read</strong> to hear the current page read aloud.</li>
                  <li>Use <strong>Podcast</strong> mode to listen to the entire book continuously.</li>
                </ul>
                <p className="bm-help-contact">Questions? Contact <strong>avikaventures.info@gmail.com</strong></p>
              </div>
            )}

            {/* Append a further document to the end of this book */}
            <div className="bm-append-doc">
              <h3>{strings.bookManager.appendDocHeading}</h3>
              <p className="bm-doc-hint bm-append-hint">{strings.bookManager.appendDocHint}</p>
              <div className="bm-append-row">
                <input
                  type="file"
                  accept=".pdf,.docx,.doc"
                  id="append-doc-input"
                  className="bm-file-input"
                  disabled={appendUploading}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    // Clear immediately so picking the same file twice still fires onChange.
                    e.target.value = "";
                    if (!file) return;
                    setAppendUploading(true);
                    try {
                      const formData = new FormData();
                      formData.append("file", file);
                      const res = await api.post(`${API}/${selectedBook.id}/append-document`, formData, {
                        headers: { "Content-Type": "multipart/form-data" },
                      });
                      const added = (res.data.pages || []).length - pages.length;
                      setSelectedBook(res.data);
                      setPages(res.data.pages || []);
                      showMessage(strings.bookManager.msgPagesAppended(added));
                    } catch (err) {
                      if (!isUpgradeRequiredError(err)) {
                        showMessage(strings.bookManager.msgAppendFailed(err.response?.data?.error || err.message));
                      }
                    } finally {
                      setAppendUploading(false);
                    }
                  }}
                />
                <label htmlFor="append-doc-input" className={`bm-btn bm-btn-edit${appendUploading ? " bm-btn-disabled" : ""}`}>
                  {appendUploading ? strings.bookManager.appendingDoc : strings.bookManager.appendDocButton}
                </label>
              </div>
            </div>

            {/* Add page form */}
            <div className="bm-add-page">
              <h3>{strings.bookManager.addNewPage}</h3>
              <div className="bm-page-form-grid">
                <div className="bm-form-row">
                  <input type="number" placeholder={strings.bookManager.placeholderPageNumber} value={pageNumber}
                    onChange={(e) => { setPageNumber(e.target.value); setCoverDesignData({}); }} className="bm-input bm-input-small" />
                  <textarea placeholder={strings.bookManager.placeholderContent} value={pageContent}
                    onChange={(e) => setPageContent(e.target.value)} className="bm-input bm-textarea" rows={3} />
                </div>

                {/* Show Cover/Back Page Designer for cover or back page (Premium+ or Super Admin) */}
                {pageNumber && isCoverOrBack(pageNumber) && canCustomizeCover && (
                  <CoverPageDesigner
                    type={isCoverPage(pageNumber) ? "cover" : "back"}
                    bookTitle={selectedBook?.title}
                    authorName={selectedBook?.authorName}
                    imageUrl={pageImageUrl}
                    onImageChange={setPageImageUrl}
                    onDesignDataChange={(d) => setCoverDesignData(d)}
                    initialData={coverDesignData}
                  />
                )}
                {pageNumber && isCoverOrBack(pageNumber) && !canCustomizeCover && (
                  <div className="bm-upgrade-notice" style={{ padding: "16px", background: "rgba(37,99,235,0.1)", borderRadius: "8px", border: "1px solid #2a4a6b", marginTop: "8px", color: "#94a3b8", fontSize: "0.9rem" }}>
                    <strong style={{ color: "#fbbf24" }}>Cover Page Customization</strong> is available for Premium plan and above.{" "}
                    <Link to="/pricing" style={{ color: "#f59e0b", textDecoration: "underline" }}>Upgrade your plan</Link> to unlock this feature.
                  </div>
                )}

                {/* Show regular page editor for non-cover/back pages */}
                {(!pageNumber || !isCoverOrBack(pageNumber)) && (
                  <>
                    {renderFormatToolbar(
                      formatFontFamily, setFormatFontFamily,
                      formatFontSize, setFormatFontSize,
                      formatColor, setFormatColor
                    )}

                    <div className="bm-upload-row">
                      {renderImageUpload(strings.bookManager.image1Label, pageImageUrl, setPageImageUrl, uploading1, setUploading1, "add-img1", pageContent, generating1, setGenerating1)}
                      {renderImageUpload(strings.bookManager.image2Label, pageImageUrl2, setPageImageUrl2, uploading2, setUploading2, "add-img2", pageContent, generating2, setGenerating2)}
                    </div>

                    {(pageImageUrl || pageImageUrl2) && (
                      <PageLayoutEditor
                        imageUrl={pageImageUrl}
                        imageUrl2={pageImageUrl2}
                        content={pageContent}
                        textStyle={{ fontFamily: formatFontFamily, fontSize: formatFontSize, color: formatColor }}
                        layout={pageLayout}
                        onLayoutChange={setPageLayout}
                      />
                    )}
                  </>
                )}

                <button className="bm-btn bm-btn-create" onClick={handleAddPage}>{strings.bookManager.addPage}</button>
              </div>
            </div>

            {/* Pages list */}
            <div className="bm-pages-list">
              <h3>{strings.bookManager.pagesCount(pages.length)}</h3>
              {pages.map((page) => (
                <div key={page.id} className="bm-page-card">
                  {editingPage && editingPage.id === page.id ? (
                    <div className="bm-page-edit-form">
                      <div className="bm-form-row">
                        <input type="number" value={editingPage.pageNumber}
                          onChange={(e) => setEditingPage({ ...editingPage, pageNumber: parseInt(e.target.value) })}
                          className="bm-input bm-input-small" />
                        <textarea value={editingPage.content || ""}
                          onChange={(e) => setEditingPage({ ...editingPage, content: e.target.value })}
                          className="bm-input bm-textarea" placeholder={strings.bookManager.placeholderEditContent} rows={3} />
                      </div>

                      {/* Show Cover/Back Page Designer for cover or back page (Premium+ or Super Admin) */}
                      {isCoverOrBack(editingPage.pageNumber) && canCustomizeCover ? (
                        <CoverPageDesigner
                          type={isCoverPage(editingPage.pageNumber) ? "cover" : "back"}
                          bookTitle={selectedBook?.title}
                          authorName={selectedBook?.authorName}
                          imageUrl={editingPage.imageUrl}
                          onImageChange={(url) => setEditingPage((p) => ({ ...p, imageUrl: url }))}
                          onDesignDataChange={(d) => {
                            setEditingPage((p) => {
                              let existing = {};
                              try { existing = JSON.parse(p.format || "{}"); } catch { /* not JSON */ }
                              return { ...p, format: JSON.stringify({ ...existing, coverDesign: d }) };
                            });
                          }}
                          initialData={(() => {
                            try { return JSON.parse(editingPage.format).coverDesign || {}; } catch { return {}; }
                          })()}
                        />
                      ) : isCoverOrBack(editingPage.pageNumber) && !canCustomizeCover ? (
                        <div className="bm-upgrade-notice" style={{ padding: "16px", background: "rgba(37,99,235,0.1)", borderRadius: "8px", border: "1px solid #2a4a6b", marginTop: "8px", color: "#94a3b8", fontSize: "0.9rem" }}>
                          <strong style={{ color: "#fbbf24" }}>Cover Page Customization</strong> is available for Premium plan and above.{" "}
                          <Link to="/pricing" style={{ color: "#f59e0b", textDecoration: "underline" }}>Upgrade your plan</Link> to unlock this feature.
                        </div>
                      ) : (
                        <>
                          {renderFormatToolbar(
                            editingPage._fontFamily,
                            (val) => updateEditFormat("_fontFamily", val),
                            editingPage._fontSize,
                            (val) => updateEditFormat("_fontSize", val),
                            editingPage._color,
                            (val) => updateEditFormat("_color", val)
                          )}

                          <div className="bm-upload-row">
                            {renderImageUpload(
                              strings.bookManager.image1Label,
                              editingPage.imageUrl,
                              (url) => setEditingPage((p) => ({ ...p, imageUrl: url })),
                              editUploading1,
                              setEditUploading1,
                              "edit-img1",
                              editingPage.content,
                              editGenerating1,
                              setEditGenerating1
                            )}
                            {renderImageUpload(
                              strings.bookManager.image2Label,
                              editingPage.imageUrl2,
                              (url) => setEditingPage((p) => ({ ...p, imageUrl2: url })),
                              editUploading2,
                              setEditUploading2,
                              "edit-img2",
                              editingPage.content,
                              editGenerating2,
                              setEditGenerating2
                            )}
                          </div>

                          {(editingPage.imageUrl || editingPage.imageUrl2) && (
                            <PageLayoutEditor
                              imageUrl={editingPage.imageUrl}
                              imageUrl2={editingPage.imageUrl2}
                              content={editingPage.content}
                              textStyle={{ fontFamily: editingPage._fontFamily, fontSize: editingPage._fontSize, color: editingPage._color }}
                              layout={editingPage._layout}
                              onLayoutChange={(l) => updateEditFormat("_layout", l)}
                            />
                          )}
                        </>
                      )}

                      <div className="bm-page-actions">
                        <button className="bm-btn bm-btn-create" onClick={handleUpdatePage}>{strings.common.save}</button>
                        <button className="bm-btn bm-btn-back" onClick={() => setEditingPage(null)}>{strings.common.cancel}</button>
                      </div>
                    </div>
                  ) : (
                    <div className="bm-page-display">
                      <span className="bm-page-num">#{page.pageNumber}</span>
                      {page.pageNumber === 1 && <span className="bm-page-type-badge bm-badge-cover">Cover</span>}
                      {page.pageNumber === getBackPageNumber() && page.pageNumber !== 1 && <span className="bm-page-type-badge bm-badge-back">Back</span>}
                      <span className="bm-page-content">{page.content || strings.bookManager.emptyPage}</span>
                      {isCoverOrBack(page.pageNumber) && page.imageUrl ? (
                        <div className="bm-cover-preview" style={{ position: "relative", width: "180px", height: "240px", overflow: "hidden", borderRadius: "8px", border: "1px solid #2a4a6b" }}>
                          <img src={resolveImageUrl(page.imageUrl)} alt={strings.bookManager.image1Alt}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>
                      ) : (
                        <>
                          {page.imageUrl && (
                            <img src={resolveImageUrl(page.imageUrl)} alt={strings.bookManager.image1Alt} className="bm-page-thumb" />
                          )}
                          {page.imageUrl2 && (
                            <img src={resolveImageUrl(page.imageUrl2)} alt={strings.bookManager.image2Alt} className="bm-page-thumb" />
                          )}
                        </>
                      )}
                      <div className="bm-page-actions">
                        <button className="bm-btn bm-btn-edit" onClick={() => startEditingPage(page)}>{strings.common.edit}</button>
                        <button className="bm-btn bm-btn-delete" onClick={() => handleDeletePage(page.id)}>{strings.common.delete}</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <button className="bm-btn bm-btn-back" onClick={() => setView("menu")} style={{ marginTop: "20px" }}>
          {strings.bookManager.backToMenu}
        </button>
      </div>
    );
  }

  // Preview
  if (view === "preview" && selectedBook) {
    return (
      <div className="book-manager">
        <h1>{strings.bookManager.previewHeading(selectedBook.title)}</h1>
        <FlipBook bookId={selectedBook.id} />
        <div className="bm-preview-actions">
          <button className="bm-btn bm-btn-back" onClick={() => setView("edit")}>
            {strings.bookManager.backToEdit}
          </button>
          <button className="bm-btn bm-btn-back" onClick={() => setView("menu")}>
            {strings.bookManager.backToMenu}
          </button>
        </div>
      </div>
    );
  }

  return null;
  })();

  return (
    <TermsGate userId={userId}>
      {wrappedContent}
      {editorFile && (
        <ImageEditor
          file={editorFile}
          onDone={(editedFile) => editorCallback && editorCallback(editedFile)}
          onCancel={() => { setEditorFile(null); setEditorCallback(null); }}
        />
      )}
    </TermsGate>
  );
}

export default BookManager;
